import type { JSONSchema7 } from "json-schema";
import { pmvSquareFootageEstimateSchema } from "common/utils/formStructure";
import { Checkbox } from "../../../../../../Inputs";
import { Number } from "../../../../../../Inputs/react-hook-form";
import {
  getSchemaPropertyFormPath,
  getUiSchemaFormPath,
  useBuilderFormContext,
  useBuilderWatch,
} from "../../form/form";
import { WidgetSettingsContainer } from "./__styles__/WidgetSettings";
import { InputSettingsFormProps } from "./FieldSettingsView";
import { DisplaySettings } from "./shared/DisplaySettings";
import { FieldPermissionsConfig } from "./shared/FieldPermissionsConfig";
import { FieldSettingsConfig } from "./shared/FieldSettingsConfig";

const SQUARE_FOOTAGE_ESTIMATE_OPTION_NAME = "squareFootageEstimate";

export function PropertyMarketValueFieldSettings({
  fieldPath,
  isModuleInput,
  isReadOnly,
}: InputSettingsFormProps) {
  const { setValue, register } = useBuilderFormContext();

  const schemaOneOfPath = getSchemaPropertyFormPath(fieldPath, "oneOf");
  const uiSchemaOneOfPath = getUiSchemaFormPath([...fieldPath, "oneOf"]);

  const schemaOneOf = useBuilderWatch(schemaOneOfPath) as Array<JSONSchema7>;
  const uiSchemaOneOf = useBuilderWatch(
    uiSchemaOneOfPath,
  ) as Array<JSONSchema7>;

  const squareFootageEstimateOptionIndex =
    findSquareFootageEstimateOptionIndex(schemaOneOf);

  const includeSquareFootage = squareFootageEstimateOptionIndex !== -1;

  function addSquareFootageEstimateOption() {
    setValue(schemaOneOfPath, [
      pmvSquareFootageEstimateSchema.schema,
      ...schemaOneOf,
    ]);
    setValue(uiSchemaOneOfPath, [
      pmvSquareFootageEstimateSchema.uiSchema,
      ...uiSchemaOneOf,
    ]);
  }

  function removeSquareFootageEstimateOption() {
    setValue(
      schemaOneOfPath,
      schemaOneOf.filter(
        (_, index) => index !== squareFootageEstimateOptionIndex,
      ),
    );
    setValue(
      uiSchemaOneOfPath,
      uiSchemaOneOf.filter(
        (_, index) => index !== squareFootageEstimateOptionIndex,
      ),
    );
  }

  // ex: uiSchema.oneOf.0.costPerSqft.ui:defaultValue
  const costPerSqftDefaultValuePath =
    squareFootageEstimateOptionIndex > -1 ?
      [
        uiSchemaOneOfPath,
        squareFootageEstimateOptionIndex,
        "costPerSqft",
        "ui:defaultValue",
      ].join(".")
    : undefined;

  return (
    <WidgetSettingsContainer>
      <FieldSettingsConfig
        fieldPath={fieldPath}
        isModuleInput={isModuleInput}
        isReadOnly={isReadOnly}
        isFormulaField={false}
      />
      <Checkbox
        value={includeSquareFootage}
        label="Include Square footage estimate"
        name="includeSquareFootage"
        disabled={isReadOnly}
        onChange={checked => {
          if (checked) {
            addSquareFootageEstimateOption();
          } else {
            removeSquareFootageEstimateOption();
          }
        }}
      />
      {costPerSqftDefaultValuePath && (
        <Number
          name={costPerSqftDefaultValuePath}
          register={register}
          disabled={isReadOnly}
          label="Default cost per sqft ($)"
          data-testid="default-cost-per-sqft-input"
        />
      )}
      <FieldPermissionsConfig fieldPath={fieldPath} isReadOnly={isReadOnly} />
      <DisplaySettings fieldPath={fieldPath} isReadOnly={isReadOnly} />
    </WidgetSettingsContainer>
  );
}

function findSquareFootageEstimateOptionIndex(schemaOneOf: Array<JSONSchema7>) {
  return schemaOneOf.findIndex(
    option =>
      "name" in option && option.name === SQUARE_FOOTAGE_ESTIMATE_OPTION_NAME,
  );
}
